import type { MutationCallbacks } from '#shared'
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/vue-query'
import { unref, type MaybeRef } from 'vue'
import {
  getChatQueryKey,
  PERSONAL_CHATS_QUERY_KEY,
  PUBLIC_CHATS_QUERY_KEY,
  type GetChatsResponse,
} from '#entities/chat'
import { authInProtectedChat } from '../api/auth-in-protected-chat'
import type { AuthInProtectedChatResponse } from '../models/auth-in-protected-chat-response'

type ChatAuthMutationCallbacks = MutationCallbacks<string, AuthInProtectedChatResponse>

export function useChatAuthMutation(chatId: MaybeRef<number>, callbacks: ChatAuthMutationCallbacks = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    ...callbacks,

    mutationFn: (password: string) => authInProtectedChat(unref(chatId), password),

    onSuccess: (response, payload) => {
      queryClient.setQueryData(PUBLIC_CHATS_QUERY_KEY, (queryData: GetChatsResponse | undefined) => {
        if (!queryData) return

        const newData = queryData?.data.map(chat => {
          if (chat.id === unref(chatId)) {
            return response.data
          }

          return chat
        })

        return { ...queryData, data: newData }
      })

      queryClient.setQueryData(PERSONAL_CHATS_QUERY_KEY, (queryData: InfiniteData<GetChatsResponse> | undefined) => {
        if (!queryData) return

        const newPages = queryData.pages.map(page => {
          if (!page.data.some(chat => chat.id === unref(chatId))) return page

          return {
            ...page,
            data: page.data.map(chat => {
              if (chat.id !== unref(chatId)) return chat

              return response.data
            }),
          }
        })

        return {
          ...queryData,
          pages: newPages,
        }
      })

      queryClient.invalidateQueries({ queryKey: getChatQueryKey(unref(chatId)) })

      callbacks.onSuccess?.(response, payload)
    },
  })
}
